/**
 * Toast notification component for displaying success, error, and info messages.
 * Animates in from top-right, auto-dismisses after configurable duration.
 */

import { useEffect, useState } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  onDismiss: (id: string) => void;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const VARIANT_STYLES: Record<ToastVariant, { bg: string; border: string; text: string; icon: string }> = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    icon: 'ri-check-line text-green-600',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    icon: 'ri-error-warning-line text-red-600',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    icon: 'ri-information-line text-blue-600',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    icon: 'ri-alert-line text-amber-600',
  },
};

export default function Toast({
  id,
  message,
  variant,
  duration = 5000,
  onDismiss,
  action,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const styles = VARIANT_STYLES[variant];

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = setTimeout(() => setIsVisible(true), 10);

    // Auto-dismiss after duration
    const dismissTimer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(dismissTimer);
    };
  }, [duration]);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(id);
    }, 300); // Match animation duration
  };

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={`
        flex items-start gap-3 p-4 rounded-lg border shadow-lg
        transform transition-[transform,opacity] duration-300 ease-out
        ${styles.bg} ${styles.border}
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <i className={`${styles.icon} text-xl flex-shrink-0 mt-0.5`} aria-hidden="true" />

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${styles.text}`}>{message}</p>

        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className={`mt-2 text-sm font-medium underline hover:no-underline ${styles.text} cursor-pointer`}
          >
            {action.label}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={handleDismiss}
        className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        aria-label="Dismiss notification"
      >
        <i className="ri-close-line text-lg" aria-hidden="true" />
      </button>
    </div>
  );
}
